import { Brain, Zap } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import IntelligentOptimization from '@/components/IntelligentOptimization';
import { motion } from 'framer-motion';

const IntelligentOptimizationSection = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="section relative py-24 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-900/10 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 relative"
      >
        <div className="flex flex-col space-y-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Feature badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500/10 to-blue-500/10 backdrop-blur-sm border border-indigo-500/20">
              <Zap className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">
                Self-Learning AI
              </span>
            </div>
            
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              viewport={{ once: true }}
              className="relative inline-block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-blue-500 blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-indigo-500/20 to-blue-500/20 backdrop-blur-sm border border-indigo-500/30 p-4 rounded-2xl">
                <Brain size={isMobile ? 28 : 32} className="text-white" />
              </div>
            </motion.div>
            
            <h2 className="text-3xl md:text-4xl font-bold">
              <span className="text-white">Intelligent</span>{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent animate-gradient-x">
                Self-Optimization
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Watch your campaigns evolve and improve{' '}
              <span className="text-white font-medium">automatically with AI-driven optimization</span>{' '}
              that learns from every interaction.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 to-blue-500/20 blur-3xl opacity-60" />
            
            {/* Glass morphism container */}
            <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 p-1 shadow-2xl hover:shadow-indigo-500/20 transition-all duration-300">
              <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 rounded-xl p-6">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/5 to-blue-500/5 rounded-xl" />
                <div className="relative">
                  <IntelligentOptimization />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default IntelligentOptimizationSection;
